const DEFAULT_FILE_NAME = 'download';

/**
 * How long the fetch is given before the file is handed to the browser instead. A request
 * that never settles would otherwise hold the download open forever, with the fallback
 * below never reached.
 */
const FETCH_TIMEOUT_MS = 30_000;

/**
 * Installed to the home screen, where the browser's own download UI is not there to take a
 * file and there is no tab to come back from. `navigator.standalone` is Safari's own flag,
 * predating the display-mode query it still does not fully support.
 */
function isStandalone(): boolean {
  const navigatorWithLegacyFlag = window.navigator as Navigator & { standalone?: boolean };

  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    navigatorWithLegacyFlag.standalone === true
  );
}

/** Hands the file to the OS, which offers "Save to Files" among the usual targets. */
async function shareFile(blob: Blob, fileName: string): Promise<boolean> {
  const file = new File([blob], fileName, { type: blob.type });

  // Sharing needs a secure context: over plain HTTP the API is not there at all.
  if (!window.navigator.canShare?.({ files: [file] })) {
    return false;
  }

  try {
    await window.navigator.share({ files: [file], title: fileName });
  } catch (error) {
    // Dismissing the share sheet is a choice, not a failure to fall back from.
    if ((error as Error)?.name !== 'AbortError') {
      return false;
    }
  }

  return true;
}

function saveAsLink(blob: Blob, fileName: string): void {
  const objectUrl = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  // Firefox follows a link only while it is in the document.
  document.body.append(link);
  link.click();
  link.remove();

  // The click only starts the download; revoking in the same task can cut it off.
  setTimeout(() => URL.revokeObjectURL(objectUrl));
}

/**
 * Whether a file can be got out of the app here, for callers deciding whether to offer the
 * action. A standalone PWA has only the share sheet, which needs a secure context.
 */
export function canSaveFile(): boolean {
  return !isStandalone() || typeof window.navigator.canShare === 'function';
}

/**
 * Saves a file under a chosen name.
 *
 * In a browser the file is downloaded: fetched and saved as a blob, because storage serves it
 * from another origin where the `download` attribute is ignored. A standalone PWA has no
 * download UI of its own, so there the file goes to the share sheet instead.
 */
export async function downloadFile(src: string, fileName?: string): Promise<void> {
  if (!src) {
    return;
  }

  try {
    const response = await fetch(src, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });

    // fetch only rejects on network errors, so an HTTP error (404/500) must be
    // turned into a throw to reach the window.open fallback below instead of
    // saving the error-page body as the file.
    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.status}`);
    }

    const blob = await response.blob();
    const name = fileName || DEFAULT_FILE_NAME;

    if (isStandalone()) {
      // The share sheet is the only way out of an installed app, so a failed share is the
      // end of the road: an anchor would navigate to the file and replace the app, with no
      // browser chrome to come back from. `canSaveFile` keeps the action off screen where
      // sharing is unavailable altogether.
      await shareFile(blob, name);

      return;
    }

    saveAsLink(blob, name);
  } catch {
    // Not in a standalone PWA: there the app itself is what would be replaced.
    if (!isStandalone()) {
      window.open(src, '_blank', 'noopener');
    }
  }
}
